import { API } from "../services/api";

// Facturation chronique d'un patient : un événement CHRONIC dont la description encode le médicament, le produit,
// le motif et le commentaire. Règles communes à l'Administration et aux écrans de contrôle.

const ADMIN_API_CONFIG = { noRedirectOn401: true };

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export const CHRONIC_REASON_OPTIONS = [
    "",
    "Refus crédit",
    "PHN",
    "Pas Ald/AMC",
    "Arrêt",
    "Pas ordo sécurisé",
    "Pas ordo exception",
];

export const CHRONIC_REASON_SEPARATOR = " · Motif: ";

export const CHRONIC_COMMENT_SEPARATOR = " · Commentaire: ";

export const LEGACY_CHRONIC_BILLING_DATE_SEPARATOR = " · Date facturation: ";

export const CHRONIC_MEDICAMENT_ID_PREFIX = "[medicament:";

export const CHRONIC_MEDICAMENT_ID_SUFFIX = "]";

export function parseChronicDescription(description) {
    const value = String(description || "");
    const medicamentMatch = value.match(/^\[medicament:(\d+)]\s*/);
    let withoutMedicamentId = medicamentMatch ? value.slice(medicamentMatch[0].length) : value;
    let comment = "";
    let billingDate = "";
    const commentIndex = withoutMedicamentId.indexOf(CHRONIC_COMMENT_SEPARATOR);
    if (commentIndex >= 0) {
        comment = withoutMedicamentId.slice(commentIndex + CHRONIC_COMMENT_SEPARATOR.length).trim();
        withoutMedicamentId = withoutMedicamentId.slice(0, commentIndex);
    }
    const billingDateIndex = withoutMedicamentId.indexOf(LEGACY_CHRONIC_BILLING_DATE_SEPARATOR);
    if (billingDateIndex >= 0) {
        billingDate = withoutMedicamentId.slice(billingDateIndex + LEGACY_CHRONIC_BILLING_DATE_SEPARATOR.length).trim();
        withoutMedicamentId = withoutMedicamentId.slice(0, billingDateIndex);
    }
    const [productInfo, reason = ""] = withoutMedicamentId.split(CHRONIC_REASON_SEPARATOR);
    return {
        productInfo: productInfo.trim(),
        reason: CHRONIC_REASON_OPTIONS.includes(reason.trim()) ? reason.trim() : "",
        comment,
        medicamentId: medicamentMatch ? medicamentMatch[1] : null,
        billingDate: billingDate.slice(0, 10),
    };
}

export function formatChronicDescription(productInfo, reason, medicamentId = null, comment = "") {
    const cleanInfo = String(productInfo || "").trim();
    const cleanReason = CHRONIC_REASON_OPTIONS.includes(reason) ? reason : "";
    const cleanComment = String(comment || "").trim();
    const prefix = medicamentId ? `${CHRONIC_MEDICAMENT_ID_PREFIX}${medicamentId}${CHRONIC_MEDICAMENT_ID_SUFFIX} ` : "";
    const withReason = cleanReason ? `${prefix}${cleanInfo}${CHRONIC_REASON_SEPARATOR}${cleanReason}` : `${prefix}${cleanInfo}`;
    return cleanComment ? `${withReason}${CHRONIC_COMMENT_SEPARATOR}${cleanComment}` : withReason;
}

export function manualChronicProductEventDescription({ name, quantity, reason }) {
    const parts = [String(name || "").trim()];
    if (String(quantity || "").trim()) parts.push(`Qté ${String(quantity).trim()}`);
    parts.push("Ajout manuel");
    parts.push("Validation facturation non faite");
    return formatChronicDescription(parts.join(" · "), reason);
}

export function chronicProductName(product) {
    return [product?.nom, product?.dosage].filter(Boolean).join(" ") || "Produit sans nom";
}

export function chronicProductEventDescription(product) {
    const parts = [chronicProductName(product)];
    if (product?.quantite) parts.push(`Qté ${product.quantite}`);
    if (product?.commandeId) parts.push(`Commande #${product.commandeId}`);
    parts.push(product?.billingValidated ? "Validation facturation faite" : "Validation facturation non faite");
    return formatChronicDescription(parts.join(" · "), "", product?.id);
}

export function isChronicProductInfoBilled(productInfo) {
    return /Facturé le|Validation facturation faite/i.test(String(productInfo || ""));
}

export function isChronicBillingEventBilled(event) {
    return isChronicProductInfoBilled(parseChronicDescription(event?.description).productInfo);
}

export function markChronicProductInfoBilled(productInfo, product) {
    const billedAt = formatDateTime(product.billingHour) || formatDateTime(new Date().toISOString());
    const operator = product.billingUsername || "opérateur inconnu";
    const cleaned = String(productInfo || "")
        .replace("Validation facturation non faite", "Validation facturation faite")
        .replace(/\s·\sFacturé le .*/i, "");
    return `${cleaned} · Facturé le ${billedAt} par ${operator}`;
}

export function existingControleChronicProductIds(patient) {
    return new Set(
        (patient?.billingEvents || [])
            .filter(event => event.type === "CHRONIC")
            .map(event => parseChronicDescription(event.description).medicamentId)
            .filter(Boolean)
            .map(String)
    );
}

export async function resolveChronicMedicamentId(patientId, productInfo) {
    const response = await API.get(`/medicaments/patients/${patientId}/traitements?page=0&size=100`, ADMIN_API_CONFIG);
    const data = response.data || {};
    const products = Array.isArray(data) ? data : data.content || [];
    // Le même produit peut figurer sur plusieurs commandes : la commande citée dans la ligne départage.
    const commandeMatch = String(productInfo || "").match(/Commande #(\d+)/);
    const commandeId = commandeMatch ? Number(commandeMatch[1]) : null;
    const target = normalizeProductText(productInfo);
    const found = products.find(product => {
        const sameCommande = !commandeId || Number(product.commandeId) === commandeId;
        return sameCommande && target.includes(normalizeProductText(chronicProductName(product)));
    });
    return found?.id || null;
}

function normalizeProductText(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
}
